// Components
import { useTheme } from 'lib/context/ThemeContext';
import { getEnum } from 'lib/utils/get-enum';

import useExperienceEditor from 'lib/utils/use-experience-editor';
import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';
import { BodyCopy } from 'src/helpers/BodyCopy';
import { ButtonVariants, cta1ToButtonProps, cta2ToButtonProps } from 'src/helpers/Button';
import ButtonGroup from 'src/helpers/ButtonGroup/ButtonGroup';
import Component from 'src/helpers/Component/Component';
import { ComponentBackgroundVariants } from 'src/helpers/Component/Component';
import Headline from 'src/helpers/Headline/Headline';
import ImagePrimary from 'src/helpers/Media/ImagePrimary';
import { Subheadline } from 'src/helpers/Subheadline';

import { Field, RichText } from '@sitecore-jss/sitecore-jss-nextjs';
import { themePromoBannerAuthored } from './PromoBannerAuthored.theme';

export type PromoBannerAuthoredProps =
  Feature.EnterpriseWeb.Enterprise.Components.Promo.PromoBanner.PromoBannerAuthored;

export type HeadingLevel = 'h2' | 'h3' | 'h4';
export type TextAlignment = 'left' | 'center';
export type ImagePosition = 'right' | 'left';
export type BorderStyle = 'default' | 'dark' | 'light';
export type ComponentStyle =
  | 'brand-color-solid'
  | 'black-outline'
  | 'black-solid'
  | 'brand-color-outline';

const LegalCopyPartial = (
  condition: boolean,
  classNames: string,
  copyField: Field<string>
): JSX.Element => {
  const isEE = useExperienceEditor();

  if ((!copyField || copyField?.value === '') && !isEE) {
    return <></>;
  }

  return (
    <>
      {condition && (
        <div className={classNames}>
          <RichText field={copyField} />
        </div>
      )}
    </>
  );
};

const MapAWComponentStyleToBackgroundVariant = (
  componentStyle: ComponentStyle
): ComponentBackgroundVariants => {
  switch (componentStyle) {
    case 'brand-color-solid':
      return 'primary';
    case 'black-solid':
      return 'secondary';
  }

  return 'white';
};

const MapRBAComponentStyleToBackgroundVariant = (
  componentStyle: ComponentStyle
): ComponentBackgroundVariants => {
  switch (componentStyle) {
    case 'brand-color-solid':
      return 'primary';
    case 'black-solid':
      return 'secondary';
  }

  return 'white';
};

const PromoBannerAuthored = (props: PromoBannerAuthoredProps): JSX.Element => {
  const hasImage = !!props.fields?.primaryImage?.value?.src;

  const imagePosition = getEnum<ButtonVariants>(props.fields?.imgPosition) ?? 'right';
  const ctaRightAlign =
    (hasImage && imagePosition === 'right') ||
    (!hasImage && props.fields?.ctaRightAlign?.value === true);
  const componentStyle =
    getEnum<ComponentStyle>(props.fields?.componentStyle) ?? 'brand-color-solid';
  const ctaOneExists = !!props.fields?.cta1Link?.value?.href;
  const ctaTwoExists = !!props.fields?.cta2Link?.value?.href;
  const ctaCount = ctaOneExists && ctaTwoExists ? 2 : ctaOneExists || ctaTwoExists ? 1 : 0;

  const isBlackSolid = componentStyle === 'black-solid';
  const isFullBleed =
    props.fields?.fullBleedOnSolidColor?.value === true &&
    (componentStyle === 'brand-color-solid' || componentStyle === 'black-solid')
      ? true
      : false;

  const { themeName, themeData } = useTheme(
    themePromoBannerAuthored(componentStyle, ctaRightAlign, hasImage, ctaCount)
  );

  const isAW = themeName === 'aw';
  const style = isAW
    ? MapAWComponentStyleToBackgroundVariant(componentStyle)
    : MapRBAComponentStyleToBackgroundVariant(componentStyle);

  return (
    <Component
      variant="lg"
      sectionWrapperClasses={
        isFullBleed && !isBlackSolid
          ? 'bg-primary'
          : '' || (isFullBleed && isBlackSolid)
          ? 'bg-black'
          : ''
      }
      gap="gap-x-0"
      padding="px-0"
      dataComponent="promo/promobannerauthored"
      backgroundVariant={style}
      {...props}
    >
      <div className={themeData.classes.contentClasses.contentWrapperClass}>
        {hasImage && (
          <div className={themeData.classes.contentClasses.imageContainerClass}>
            <ImagePrimary
              {...props}
              imageLayout="responsive"
              ratio={themeData.classes.contentClasses.imageRatio}
            />
          </div>
        )}
        <div className={themeData.classes.contentClasses.copyContainerClass}>
          <Headline classes={themeData.classes.firstHeadline.headlineContainer} {...props} />
          {props.fields?.subheadlineText && (
            <Subheadline
              classes={themeData.classes.contentClasses?.subHeadlineContainer}
              {...props}
            />
          )}
          {props.fields?.body && (
            <BodyCopy classes={themeData.classes.contentClasses?.body} {...props} />
          )}
          {(hasImage || !ctaRightAlign) && (ctaOneExists || ctaTwoExists) && (
            <ButtonGroup
              cta1={cta1ToButtonProps(props, themeData.classes.buttonGroupClass.cta1Classes)}
              cta2={cta2ToButtonProps(props, themeData.classes.buttonGroupClass.cta2Classes)}
              wrapperClasses={themeData.classes.buttonGroupClass.wrapper}
            />
          )}
          {LegalCopyPartial(
            !isAW,
            themeData.classes.contentClasses.legalCopyClass,
            props.fields?.legalCopy
          )}
        </div>
        {!hasImage && ctaRightAlign && (ctaOneExists || ctaTwoExists) && (
          <ButtonGroup
            cta1={cta1ToButtonProps(props, themeData.classes.buttonGroupClass.cta1Classes)}
            cta2={cta2ToButtonProps(props, themeData.classes.buttonGroupClass.cta2Classes)}
            wrapperClasses={themeData.classes.buttonGroupClass.wrapper}
          />
        )}
      </div>
      {LegalCopyPartial(
        isAW,
        themeData.classes.contentClasses.legalCopyClass,
        props.fields?.legalCopy
      )}
    </Component>
  );
};

export default PromoBannerAuthored;
